import { useState, useEffect, useRef, useCallback } from "react";
import { useParams, useBlocker } from "@tanstack/react-router";
import { Card } from "react-bootstrap";
import { LuBuilding2, LuPencil, LuPlus } from "react-icons/lu";

import { User, UserOrg } from "@/models/user.tsx";
import { useCoreData, useChildEntities, useEntity } from "@/queries.ts";
import { CoreDataModel } from "@/models";
import Loader from "@/components/shared/Loader";
import { UserOrgPermissionsCard } from "./UserOrgPermissionsCard";
import { AddNewOrgModal } from "./AddNewOrgModal";

function getRoleCodes(org: UserOrg, coreData: CoreDataModel): string[] {
  if (!coreData?.securityRoles) return [];
  const idToCode = Object.fromEntries(
    coreData.securityRoles.map((r) => [r.CAPSDACRoleId, r.CAPSDACRoleCode])
  );
  return (org.roleIds ?? []).map((id) => idToCode[id]).filter(Boolean);
}

function getDataAccessSummary(org: UserOrg, coreData: CoreDataModel): string {
  if (!coreData?.securityRoles) return "—";
  const codes = getRoleCodes(org, coreData);
  const adminRole = coreData.securityRoles.find(
    (r) => r.CAPSDACRoleCode === "ADMIN" && r.ScrtyGrpKey === org.orgType
  );
  if (adminRole && org.roleIds?.includes(adminRole.CAPSDACRoleId))
    return "Administrator";
  const orgRoles = coreData.securityRoles.filter(
    (r) => r.ScrtyGrpKey === org.orgType
  );
  const totalRecordTypes = [
    ...new Set(
      orgRoles
        .filter(
          (r) =>
            r.CAPSDACRoleCode.endsWith("View") ||
            r.CAPSDACRoleCode.endsWith("Edit")
        )
        .map((r) => r.CAPSDACRoleCode.replace(/View$|Edit$/, ""))
    ),
  ];
  const hasEdit = totalRecordTypes.filter((rt) => codes.includes(`${rt}Edit`));
  const hasView = totalRecordTypes.filter((rt) => codes.includes(`${rt}View`));
  if (hasEdit.length === totalRecordTypes.length) return "Full Access";
  if (hasView.length === totalRecordTypes.length) return "All View";
  if (hasEdit.length > 0)
    return `${hasEdit.length} Edit, ${hasView.length} View`;
  if (hasView.length > 0) return `${hasView.length} View`;
  return "No Access";
}

function getAdditionalSummary(org: UserOrg, coreData: CoreDataModel): string {
  if (!coreData?.securityRoles) return "—";
  const codes = getRoleCodes(org, coreData);
  const orgRoles = coreData.securityRoles.filter(
    (r) => r.ScrtyGrpKey === org.orgType
  );
  const additionalPermissions = orgRoles
    .filter(
      (r) =>
        !r.CAPSDACRoleCode.endsWith("View") &&
        !r.CAPSDACRoleCode.endsWith("Edit") &&
        r.CAPSDACRoleCode !== "ADMIN"
    )
    .map((r) => r.CAPSDACRoleCode);
  const active = additionalPermissions.filter((p) => codes.includes(p));
  if (active.length === 0) return "None";
  if (active.length === additionalPermissions.length) return "All";
  return `${active.length} of ${additionalPermissions.length}`;
}

export function UserOrganizationsInline({
  user,
  isAdmin,
}: {
  user: User<UserOrg>;
  isAdmin: boolean;
}) {
  const params = useParams({ strict: false });
  const coreData = useCoreData();
  const childrenResult = useChildEntities(params.leaid!);
  const entityResult = useEntity(params.leaid!);
  const [showModal, setShowModal] = useState(false);
  const [editOrg, setEditOrg] = useState<UserOrg | null>(null);
  const [showEditLeaveConfirm, setShowEditLeaveConfirm] = useState(false);
  const editDialogRef = useRef<HTMLDivElement>(null);

  const isStateLevel = params.leaid === "0-";

  const [dirtyOrgs, setDirtyOrgs] = useState<Set<number>>(new Set());
  const hasUnsavedChanges = dirtyOrgs.size > 0;

  const handleDirtyChange = useCallback((userOrgId: number, dirty: boolean) => {
    setDirtyOrgs((prev) => {
      const next = new Set(prev);
      if (dirty) next.add(userOrgId);
      else next.delete(userOrgId);
      return next;
    });
  }, []);

  const blocker = useBlocker({ condition: hasUnsavedChanges });
  const showLeaveConfirm = blocker.status === "blocked";

  useEffect(() => {
    if (editOrg !== null) {
      editDialogRef.current?.focus();
    }
  }, [editOrg]);

  const tryCloseEditDialog = () => {
    if (
      editOrg &&
      editOrg.userOrgId != null &&
      dirtyOrgs.has(editOrg.userOrgId)
    ) {
      setShowEditLeaveConfirm(true);
    } else {
      setEditOrg(null);
    }
  };

  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (hasUnsavedChanges) {
        e.preventDefault();
        e.returnValue = "";
      }
    };
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [hasUnsavedChanges]);

  if (entityResult.isPending || childrenResult.isPending)
    return <Loader containerClass="py-4" />;
  if (entityResult.error)
    return <div>An error has occurred: {entityResult.error.message}</div>;
  if (childrenResult.error)
    return <div>An error has occurred: {childrenResult.error.message}</div>;

  const stateOrg = user.userOrgs?.find((o) => o.orgType === 2);
  const leaOrgs = (user.userOrgs?.filter((o) => o.orgType === 1) ?? []).sort(
    (a, b) => (a.orgName ?? "").localeCompare(b.orgName ?? "")
  );

  if (isStateLevel) {
    return (
      <div>
        {stateOrg && (
          <div className="mb-5">
            <UserOrgPermissionsCard
              key={stateOrg.userOrgId}
              user={user}
              org={stateOrg}
              isAdmin={isAdmin}
              leaid={params.leaid!}
              coreData={coreData}
              onDirtyChange={handleDirtyChange}
            />
          </div>
        )}

        <div
          className="d-flex align-items-center justify-content-between mb-3"
          style={{ marginTop: "3.5rem" }}
        >
          <div
            className="d-flex align-items-center gap-2 fw-semibold"
            style={{
              fontSize: "0.875rem",
              letterSpacing: "0.06em",
              textTransform: "uppercase",
              color: "#1e293b",
            }}
          >
            <LuBuilding2 size={16} style={{ color: "#475569" }} />
            User Organizations
          </div>
          {isAdmin && (
            <button
              type="button"
              className="d-inline-flex align-items-center gap-2"
              onClick={() => setShowModal(true)}
              style={{
                borderRadius: "6px",
                padding: "6px 12px",
                border: "1px solid #0F2A4A",
                color: "#0F2A4A",
                backgroundColor: "white",
                fontSize: "0.875rem",
                fontWeight: 600,
                cursor: "pointer",
                lineHeight: 1.5,
              }}
            >
              <LuPlus size={14} />
              Grant Permissions
            </button>
          )}
        </div>

        {leaOrgs.length === 0 ? (
          <Card className="card-border p-4 text-muted text-center shadow">
            No organizations assigned to this user.
          </Card>
        ) : (
          <Card className="no-padding card-border overflow-hidden shadow">
            <div className="p-3">
              <table
                className="table table-hover mb-0"
                style={{ fontSize: "0.925rem" }}
              >
                <thead>
                  <tr
                    style={{
                      color: "#1e293b",
                      fontSize: "0.82rem",
                      letterSpacing: "0.06em",
                      textTransform: "uppercase",
                    }}
                  >
                    <th className="fw-bold border-0 pb-2">Organization</th>
                    <th className="fw-bold border-0 pb-2">Data Access</th>
                    <th className="fw-bold border-0 pb-2">Additional</th>
                    <th className="fw-bold border-0 pb-2 text-end">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {leaOrgs.map((org) => (
                    <tr key={org.userOrgId} style={{ verticalAlign: "middle" }}>
                      <td className="py-3">
                        <div className="d-flex align-items-center gap-2">
                          <LuBuilding2
                            size={15}
                            style={{ color: "#475569", flexShrink: 0 }}
                          />
                          <span
                            className="fw-semibold"
                            style={{ color: "#1e293b", fontSize: "0.925rem" }}
                          >
                            {org.orgName ?? "—"}
                          </span>
                        </div>
                      </td>
                      <td
                        className="py-3 fw-semibold"
                        style={{ color: "#1e293b" }}
                      >
                        {getDataAccessSummary(org, coreData)}
                      </td>
                      <td
                        className="py-3 fw-semibold"
                        style={{ color: "#1e293b" }}
                      >
                        {getAdditionalSummary(org, coreData)}
                      </td>
                      <td className="py-3 text-end">
                        {isAdmin && (
                          <button
                            type="button"
                            className="btn btn-sm d-inline-flex align-items-center gap-2"
                            style={{
                              borderRadius: "6px",
                              padding: "6px 12px",
                              border: "1px solid #0F2A4A",
                              color: "#0F2A4A",
                              backgroundColor: "white",
                            }}
                            onClick={() => setEditOrg(org)}
                          >
                            <LuPencil size={14} />
                            <span>Edit</span>
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        )}

        {editOrg !== null && (
          <div
            ref={editDialogRef}
            tabIndex={-1}
            role="dialog"
            aria-modal="true"
            aria-labelledby="edit-org-title"
            onKeyDown={(e) => e.key === "Escape" && tryCloseEditDialog()}
            style={{
              position: "fixed",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: "rgba(0,0,0,0.5)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              zIndex: 1050,
            }}
            onClick={tryCloseEditDialog}
          >
            <span id="edit-org-title" className="visually-hidden">
              Edit Permissions — {editOrg.orgName}
            </span>
            <div
              style={{
                width: "100%",
                maxWidth: "1000px",
                maxHeight: "90vh",
                overflowY: "auto",
                borderRadius: "8px",
                position: "relative",
                boxShadow: "0 8px 30px rgba(0,0,0,0.3)",
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <button
                type="button"
                onClick={tryCloseEditDialog}
                className="btn-close"
                aria-label="Close"
                style={{
                  position: "absolute",
                  top: 16,
                  right: 16,
                  zIndex: 10,
                  filter: "invert(1) grayscale(100%) brightness(200%)",
                  opacity: 1,
                }}
              />
              <UserOrgPermissionsCard
                key={editOrg.userOrgId}
                user={user}
                org={editOrg}
                isAdmin={isAdmin}
                leaid={params.leaid!}
                coreData={coreData}
                onDirtyChange={handleDirtyChange}
              />
            </div>
          </div>
        )}

        {showEditLeaveConfirm && (
          <LeaveConfirmModal
            onCancel={() => setShowEditLeaveConfirm(false)}
            onConfirm={() => {
              setShowEditLeaveConfirm(false);
              if (editOrg?.userOrgId != null) {
                setDirtyOrgs((prev) => {
                  const next = new Set(prev);
                  next.delete(editOrg.userOrgId!);
                  return next;
                });
              }
              setEditOrg(null);
            }}
          />
        )}

        {isAdmin && (
          <AddNewOrgModal
            show={showModal}
            onHide={() => setShowModal(false)}
            user={user}
            leaid={params.leaid!}
            coreData={coreData}
            entityResult={entityResult}
            childrenResult={childrenResult}
          />
        )}

        {showLeaveConfirm && (
          <LeaveConfirmModal
            onCancel={() => blocker.reset?.()}
            onConfirm={() => blocker.proceed?.()}
          />
        )}
      </div>
    );
  }

  return (
    <div>
      <div className="d-flex align-items-center justify-content-between mb-3">
        <div
          className="d-flex align-items-center gap-2 fw-semibold"
          style={{
            fontSize: "0.875rem",
            letterSpacing: "0.06em",
            textTransform: "uppercase",
            color: "#1e293b",
          }}
        >
          <LuBuilding2 size={16} style={{ color: "#475569" }} />
          {user.userOrgs?.length === 1
            ? "User Organization"
            : "User Organizations"}
        </div>
      </div>

      {!user.userOrgs || user.userOrgs.length === 0 ? (
        <Card className="card-border p-4 text-muted text-center shadow">
          No organizations assigned to this user.
        </Card>
      ) : (
        <div className="d-flex flex-column gap-3">
          {user.userOrgs.map((org) => (
            <UserOrgPermissionsCard
              key={org.userOrgId}
              user={user}
              org={org}
              isAdmin={isAdmin}
              leaid={params.leaid!}
              coreData={coreData}
              onDirtyChange={handleDirtyChange}
            />
          ))}
        </div>
      )}

      {showLeaveConfirm && (
        <LeaveConfirmModal
          onCancel={() => blocker.reset?.()}
          onConfirm={() => blocker.proceed?.()}
        />
      )}
    </div>
  );
}

function LeaveConfirmModal({
  onCancel,
  onConfirm,
}: {
  onCancel: () => void;
  onConfirm: () => void;
}) {
  const cancelRef = useRef<HTMLButtonElement>(null);
  const confirmRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    cancelRef.current?.focus();
  }, []);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Escape") {
      onCancel();
      return;
    }
    if (e.key === "Tab") {
      if (e.shiftKey && document.activeElement === cancelRef.current) {
        e.preventDefault();
        confirmRef.current?.focus();
      } else if (!e.shiftKey && document.activeElement === confirmRef.current) {
        e.preventDefault();
        cancelRef.current?.focus();
      }
    }
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="leave-confirm-title"
      aria-describedby="leave-confirm-message"
      onKeyDown={handleKeyDown}
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: "rgba(0, 0, 0, 0.85)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 9999,
      }}
    >
      <div
        style={{
          background: "white",
          padding: "32px 40px",
          borderRadius: "8px",
          maxWidth: "480px",
          width: "90%",
          boxShadow: "0 4px 20px rgba(0,0,0,0.3)",
        }}
      >
        <h5
          id="leave-confirm-title"
          style={{
            fontSize: "16px",
            fontWeight: "600",
            color: "#333",
            margin: "0 0 12px 0",
          }}
        >
          You have unsaved changes
        </h5>
        <p
          id="leave-confirm-message"
          style={{
            fontSize: "14px",
            color: "#666",
            lineHeight: "1.5",
            margin: "0 0 24px 0",
          }}
        >
          You have unsaved permission changes. If you leave now, your changes
          will be lost. This action cannot be undone.
        </p>
        <div
          style={{ display: "flex", justifyContent: "flex-end", gap: "12px" }}
        >
          <button
            ref={cancelRef}
            onClick={onCancel}
            style={{
              padding: "8px 20px",
              background: "white",
              border: "1px solid #ddd",
              borderRadius: "4px",
              color: "#333",
              fontSize: "14px",
              cursor: "pointer",
              fontWeight: "500",
            }}
          >
            Stay
          </button>
          <button
            ref={confirmRef}
            onClick={onConfirm}
            style={{
              padding: "8px 20px",
              background: "#1e5a78",
              border: "none",
              borderRadius: "4px",
              color: "white",
              fontSize: "14px",
              cursor: "pointer",
              fontWeight: "500",
            }}
          >
            Leave Anyway
          </button>
        </div>
      </div>
    </div>
  );
}
